import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Building, 
  MapPin, 
  LayoutGrid, 
  PenTool, 
  Image,
  MessageSquare,
  Plus
} from 'lucide-react';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight" data-testid="dashboard-title">Dashboard</h1>
        <p className="text-muted-foreground">Manage your real estate properties</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4 mb-8" data-testid="stats-section">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Properties</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="total-properties-stat">{stats?.total_properties || 0}</div>
            <p className="text-xs text-muted-foreground">Properties listed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="available-properties-stat">{stats?.available_properties || 0}</div>
            <p className="text-xs text-muted-foreground">Currently available</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inquiries</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="total-inquiries-stat">{stats?.total_inquiries || 0}</div>
            <p className="text-xs text-muted-foreground">{stats?.new_inquiries || 0} new</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Blog Posts</CardTitle>
            <PenTool className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="total-blogs-stat">{stats?.total_blogs || 0}</div>
            <p className="text-xs text-muted-foreground">{stats?.published_blogs || 0} published</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks for managing your content</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Button variant="outline" asChild className="justify-start" data-testid="view-properties-btn">
            <Link to="/dashboard/properties">
              <Building className="mr-2 h-4 w-4" />
              View All Properties
            </Link>
          </Button>
          <Button variant="outline" asChild className="justify-start" data-testid="add-property-btn">
            <Link to="/dashboard/properties/create">
              <Plus className="mr-2 h-4 w-4" />
              Add New Property
            </Link>
          </Button>
          <Button variant="outline" asChild className="justify-start" data-testid="manage-blogs-btn">
            <Link to="/dashboard/blogs">
              <PenTool className="mr-2 h-4 w-4" />
              Manage Blogs
            </Link>
          </Button>
          <Button variant="outline" asChild className="justify-start" data-testid="view-inquiries-btn">
            <Link to="/dashboard/inquiries">
              <MessageSquare className="mr-2 h-4 w-4" />
              View Inquiries
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Manage Sections */}
      <Card>
        <CardHeader>
          <CardTitle>Manage Sections</CardTitle>
          <CardDescription>Quick access to core platform and content areas</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3">
            <div className="flex items-center justify-between" data-testid="properties-section">
              <div className="flex items-center gap-3">
                <Building className="w-5 h-5 text-gray-700" />
                <div>
                  <div className="font-medium">Properties</div>
                  <div className="text-xs text-muted-foreground">Manage listings</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {stats?.total_properties || 0}
                </span>
                <Button asChild variant="ghost" size="icon">
                  <Link to="/dashboard/properties">
                    <LayoutGrid className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between" data-testid="blogs-section">
              <div className="flex items-center gap-3">
                <PenTool className="w-5 h-5 text-gray-700" />
                <div>
                  <div className="font-medium">Blogs</div>
                  <div className="text-xs text-muted-foreground">Create and edit posts</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {stats?.total_blogs || 0}
                </span>
                <Button asChild variant="ghost" size="icon">
                  <Link to="/dashboard/blogs">
                    <LayoutGrid className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between" data-testid="hero-slides-section">
              <div className="flex items-center gap-3">
                <Image className="w-5 h-5 text-gray-700" />
                <div>
                  <div className="font-medium">Hero Slides</div>
                  <div className="text-xs text-muted-foreground">Manage homepage slides</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button asChild variant="ghost" size="icon">
                  <Link to="/dashboard/hero-slides">
                    <LayoutGrid className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between" data-testid="inquiries-section">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-gray-700" />
                <div>
                  <div className="font-medium">Contact Inquiries</div>
                  <div className="text-xs text-muted-foreground">View customer messages</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {stats?.new_inquiries || 0} new
                </span>
                <Button asChild variant="ghost" size="icon">
                  <Link to="/dashboard/inquiries">
                    <LayoutGrid className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
